import { Domain } from '@superfunnel/types';
import Loader from '@superfunnel/ui-core/Loader';
import { QueryDocumentSnapshot } from 'firebase/firestore';
import {
  RefObject,
  useContext,
  useEffect,
  useMemo,
  useRef,
  useState,
} from 'react';
import { UserContext } from '../contexts/UserContext';
import { buildPreviewUrl } from '../utils';

type Device = 'desktop' | 'tablet' | 'mobile';

// Device viewport widths for simulating actual device rendering
const DEVICE_WIDTHS: Record<Device, number> = {
  desktop: 0, // 0 means use container width (no scaling)
  tablet: 768,
  mobile: 393, // iPhone 15/16 standard width
};

interface BaseProps {
  primaryDomain: QueryDocumentSnapshot<Domain> | undefined;
  editable?: boolean;
  id?: string;
  iframeRef?: RefObject<HTMLIFrameElement | null>;
  device?: Device;
}

interface WebsiteProps extends BaseProps {
  mode: 'website';
  dynamicPageId: string;
  variantId: string;
  websiteId: string;
}

interface GlobalSectionProps extends BaseProps {
  mode: 'globalSection';
  globalSectionId: string;
}

type Props = WebsiteProps | GlobalSectionProps;

export const WebsitePreview = ({
  primaryDomain,
  editable,
  id,
  iframeRef,
  device = 'desktop',
  ...props
}: Props) => {
  const { selectedAccount } = useContext(UserContext);
  const containerRef = useRef<HTMLDivElement>(null);
  const [containerSize, setContainerSize] = useState({ width: 0, height: 0 });

  // Calculate the device width for viewport simulation
  const deviceWidth = DEVICE_WIDTHS[device];
  const needsScaling = deviceWidth > 0;

  // Measure container size for scaling calculations
  useEffect(() => {
    if (!containerRef.current || !needsScaling) return;

    const resizeObserver = new ResizeObserver((entries) => {
      const { width, height } = entries[0].contentRect;
      setContainerSize({ width, height });
    });

    resizeObserver.observe(containerRef.current);
    return () => resizeObserver.disconnect();
  }, [needsScaling]);

  // Calculate scale factor
  const scale =
    needsScaling && containerSize.width > 0
      ? containerSize.width / deviceWidth
      : 1;

  // Calculate iframe height to fill container after scaling
  const iframeHeight =
    needsScaling && scale > 0 ? containerSize.height / scale : 0;

  // const [websitePreviewTokenError, setWebsitePreviewTokenError] = useState();

  const initialUrl = useMemo(() => {
    if (!selectedAccount) return null;
    if (!primaryDomain) return null;

    if (props.mode === 'globalSection') {
      return buildPreviewUrl(
        primaryDomain?.data().domain ?? '',
        selectedAccount.id,
        props.globalSectionId,
        props.globalSectionId,
        props.globalSectionId,
        '', // No preview token for global sections
        editable || false,
      );
    }

    const { dynamicPageId, variantId, websiteId } = props;

    if (!websiteId) return null;
    if (!dynamicPageId) return null;
    if (!variantId) return null;

    return buildPreviewUrl(
      primaryDomain.data().domain,
      selectedAccount.id,
      dynamicPageId,
      variantId,
      websiteId,
      '', // dynamicPageWebsitePreviewToken,
      editable || false,
    );
  }, [selectedAccount, primaryDomain, props, editable]);

  // useEffect(() => {
  //   if (dynamicPageWebsitePreviewToken) return;
  //   if (websitePreviewTokenError) return;
  //   if (!websiteId || !selectedAccount) return;

  //   fetchDynamicPageWebsitePreviewToken().catch((e) => {
  //     Sentry.captureException(e);
  //     setWebsitePreviewTokenError(e);
  //   });
  // }, [
  //   dynamicPageWebsitePreviewToken,
  //   websitePreviewTokenError,
  //   websiteId,
  //   selectedAccount,
  //   fetchDynamicPageWebsitePreviewToken,
  // ]);

  return (
    <>
      {initialUrl ? (
        <div
          ref={containerRef}
          style={{
            width: '100%',
            height: '100%',
            overflow: 'hidden',
          }}
        >
          <iframe
            id={id}
            ref={iframeRef}
            className="iframe-website-preview"
            data-website-id={
              props.mode === 'website' ? props.websiteId : props.globalSectionId
            }
            style={{
              border: 0,
              minHeight: 0,
              minWidth: 0,
              backgroundColor: 'white',
              ...(needsScaling
                ? {
                    width: `${deviceWidth}px`,
                    height: `${iframeHeight}px`,
                    transform: `scale(${scale})`,
                    transformOrigin: 'top left',
                  }
                : {
                    width: '100%',
                    height: '100%',
                  }),
            }}
            src={initialUrl}
          ></iframe>
        </div>
      ) : (
        <div
          style={{
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            minHeight: '100%',
          }}
        >
          <Loader />
        </div>
      )}
    </>
  );
};
